import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Search as SearchIcon, LayoutGrid, Rows3 } from "lucide-react";
import { BOOKS } from "../data/content";

// Derive thematic categories from existing book data
const CATEGORIES = [
  { id: "all", label: "All Titles", match: () => true },
  { id: "novel", label: "Novels & Travel", match: (b) => /novel|travel|romance|mekong|cobra|road/i.test(b.subtitle + " " + b.blurb) },
  { id: "history", label: "History & Chronicle", match: (b) => /history|kings|chronicle|record|past/i.test(b.title + " " + b.subtitle) },
  { id: "indochina", label: "French Indochina", match: (b) => /indochina|colonial|french|1920s|19th/i.test(b.subtitle + " " + b.blurb) },
];

export default function BooksPage() {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState("grid"); // grid | list

  const filtered = useMemo(() => {
    const C = CATEGORIES.find((c) => c.id === cat) || CATEGORIES[0];
    const n = q.trim().toLowerCase();
    return BOOKS.filter(C.match).filter((b) =>
      n ? [b.title, b.subtitle, b.author, b.blurb].join(" ").toLowerCase().includes(n) : true
    );
  }, [cat, q]);

  return (
    <div data-testid="books-page">
      {/* Editorial masthead */}
      <section className="relative pt-36 lg:pt-44 pb-14 lg:pb-20 bg-[var(--surface)] border-b border-[var(--border-c)] overflow-hidden">
        <div className="pointer-events-none absolute -right-32 -top-32 w-[36rem] h-[36rem] rounded-full bg-[var(--gold)]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--gold)]"
            >
              DatAsia Editions · Full Catalog
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.08 }}
              className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight text-[var(--text)] max-w-3xl"
            >
              The library of Khmer{" "}
              <span className="italic text-[var(--terracotta)]">history</span> &amp; literature.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.14 }}
              className="mt-6 font-sans text-base sm:text-lg leading-relaxed text-[var(--text-2)] max-w-xl"
            >
              Six landmark volumes — rare chronicles, translations and travel
              classics, meticulously edited and printed by DatAsia Press.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.2 }}
            className="lg:col-span-4"
          >
            <div className="flex items-end gap-5 pb-2 border-b-2 border-[var(--text)]">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-[var(--text-2)]">
                  In Catalog
                </div>
                <div className="font-serif text-6xl text-[var(--text)] mt-1 tabular-nums leading-none">
                  {String(BOOKS.length).padStart(2, "0")}
                </div>
              </div>
              <div className="font-hand text-2xl text-[var(--terracotta)] -rotate-2 pb-2">
                hand-set, hand-edited
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-20 z-30 bg-[var(--bg)]/90 backdrop-blur-xl border-b border-[var(--border-c)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-4 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
          <div className="flex-1 flex items-center flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`books-filter-${c.id}`}
                className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${
                  cat === c.id
                    ? "bg-[var(--ink)] text-[var(--bg)]"
                    : "border border-[var(--border-c)] text-[var(--text-2)] hover:text-[var(--text)] hover:border-[var(--text)]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-full border border-[var(--border-c)] px-4 py-2 bg-[var(--surface)]">
              <SearchIcon className="h-3.5 w-3.5 text-[var(--text-2)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search titles, authors…"
                data-testid="books-search-input"
                className="w-40 sm:w-56 bg-transparent font-sans text-sm text-[var(--text)] placeholder:text-[var(--text-2)]/50 focus:outline-none"
              />
            </div>
            <div className="hidden sm:flex items-center gap-0.5 rounded-full border border-[var(--border-c)] p-1">
              {[
                { id: "grid", icon: LayoutGrid },
                { id: "list", icon: Rows3 },
              ].map(({ id, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  data-testid={`books-view-${id}`}
                  aria-label={`${id} view`}
                  className={`h-8 w-8 rounded-full grid place-items-center transition-colors ${
                    view === id
                      ? "bg-[var(--ink)] text-[var(--bg)]"
                      : "text-[var(--text-2)] hover:text-[var(--text)]"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="relative py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <div className="flex items-baseline justify-between pb-5 mb-10 border-b border-[var(--border-c)]">
            <h2 className="font-serif text-xl text-[var(--text)]">
              Showing{" "}
              <span className="tabular-nums font-semibold">{filtered.length}</span>{" "}
              of {BOOKS.length}
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-[var(--text-2)]">
              {CATEGORIES.find((c) => c.id === cat)?.label}
            </span>
          </div>

          {filtered.length === 0 ? (
            <div className="py-24 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--text-2)]">
                Nothing matches
              </div>
              <h3 className="mt-3 font-serif text-3xl text-[var(--text)]">
                No titles in this chapter — yet.
              </h3>
              <button
                onClick={() => {
                  setCat("all");
                  setQ("");
                }}
                className="mt-6 rounded-full border border-[var(--text)] px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--text)] hover:bg-[var(--text)] hover:text-[var(--bg)] transition-colors"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {view === "grid" ? (
                <motion.div
                  key="grid"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14"
                >
                  {filtered.map((book, i) => (
                    <BookGridCard key={book.id} book={book} index={i} />
                  ))}
                </motion.div>
              ) : (
                <motion.ul
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="divide-y divide-[var(--border-c)]"
                >
                  {filtered.map((book, i) => (
                    <BookRow key={book.id} book={book} index={i} />
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          )}
        </div>
      </section>
    </div>
  );
}

const BookGridCard = ({ book, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
    whileHover={{ y: -8 }}
    data-testid="book-card-item"
    className="group flex flex-col"
  >
    <Link
      to={`/books/${book.id}`}
      data-testid={`book-link-${book.id}`}
      className="relative block overflow-hidden rounded-sm bg-[var(--surface-2)] deckle"
    >
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={book.cover}
          alt={book.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]"
        />
      </div>
      <span className="absolute inset-0 ring-1 ring-inset ring-[var(--gold)]/25" />
      <span className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.22em] bg-[var(--bg)]/95 text-[var(--text)] px-2.5 py-1 rounded-full">
        {book.year}
      </span>
      <span className="absolute bottom-4 left-1/2 -translate-x-1/2 translate-y-3 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 rounded-full bg-[var(--bg)]/95 backdrop-blur px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text)] inline-flex items-center gap-1.5">
        View Book <ArrowUpRight className="h-3 w-3" />
      </span>
    </Link>
    <div className="mt-5 flex flex-col flex-1">
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]">
        {book.author.split("·")[0].split("(")[0].trim()}
      </span>
      <h3 className="mt-2 font-serif text-2xl leading-tight text-[var(--text)]">
        <Link
          to={`/books/${book.id}`}
          className="hover:text-[var(--terracotta)] transition-colors duration-300"
        >
          {book.title}
        </Link>
      </h3>
      <p className="mt-1.5 font-sans text-sm text-[var(--text-2)] leading-snug">
        {book.subtitle}
      </p>
    </div>
  </motion.div>
);

const BookRow = ({ book, index }) => (
  <motion.li
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.05 }}
    data-testid="book-card-item"
  >
    <Link
      to={`/books/${book.id}`}
      data-testid={`book-link-${book.id}`}
      className="group grid grid-cols-[auto_80px_1fr_auto] items-start gap-5 lg:gap-8 py-7"
    >
      <span className="font-serif text-3xl text-[var(--gold)]/70 tabular-nums pt-1 w-10">
        {String(index + 1).padStart(2, "0")}
      </span>
      <div className="h-28 w-20 overflow-hidden rounded-[2px] bg-[var(--surface-2)]">
        <img
          src={book.cover}
          alt={book.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="min-w-0">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--terracotta)]">
          {book.year} · {book.author.split("·")[0].split("(")[0].trim()}
        </span>
        <h3 className="mt-1 font-serif text-2xl sm:text-3xl leading-tight text-[var(--text)] group-hover:text-[var(--terracotta)] transition-colors">
          {book.title}
        </h3>
        <p className="mt-1 font-sans text-sm text-[var(--text-2)] max-w-2xl line-clamp-2">
          {book.subtitle} — {book.blurb}
        </p>
      </div>
      <span className="hidden sm:grid h-12 w-12 rounded-full border border-[var(--border-c)] place-items-center text-[var(--text-2)] group-hover:bg-[var(--text)] group-hover:text-[var(--bg)] group-hover:border-[var(--text)] transition-colors">
        <ArrowUpRight className="h-4 w-4" />
      </span>
    </Link>
  </motion.li>
);
